import { Component, ChangeDetectionStrategy, ViewChild, ElementRef, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EditableComponent } from '@ngneat/edit-in-place/lib/editable.component';
import { Mode } from '@ngneat/edit-in-place/lib/mode';
import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { BasefieldComponent } from '../basefield/basefield.component';
import { isValidDate, toISO8601 } from '../date/date-helpers';
import { DEFAULT_PAGE_DATE_RANGE } from '@app/shared/utils/filter-form-utils';
import { DateTime } from 'luxon';

export type RdrDateRange = { start: Date; end: Date };

@Component({
  selector: 'date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComponent extends BasefieldComponent implements OnInit {
  @ViewChild('dp') dp: BsDatepickerDirective;
  @ViewChild('input') input: ElementRef;
  @ViewChild('editable') editable: EditableComponent;

  innerModel = new UntypedFormControl([]);
  isOpen = false;

  ngOnInit(): void {
    if (this.formControl.value) {
      this.setInnerModel({
        start: new Date(this.formControl.value.start as Date),
        end: new Date(this.formControl.value.end as Date),
      });
    } else if (this.props.initial?.start && this.props.initial?.end) {
      this.formControl.setValue(this.props.initial, { emitEvent: false });

      this.setInnerModel({
        start: new Date(this.props.initial.start as Date),
        end: new Date(this.props.initial.end as Date),
      });
    }
  }

  setInnerModel(dateRange: RdrDateRange | undefined) {
    if (dateRange) {
      if (isValidDate(dateRange.start) && isValidDate(dateRange.end)) {
        this.innerModel.setValue([copyDate(dateRange.start), copyDate(dateRange.end)]);
      }
    } else {
      this.innerModel.reset();
    }
  }

  onBlur() {
    if (!this.dp?.isOpen) {
      if (
        this.innerModel.value &&
        isValidDate(this.innerModel.value[0] as Date) &&
        isValidDate(this.innerModel.value[1] as Date)
      ) {
        this.formControl.setValue({
          start: copyDate(this.innerModel.value[0] as Date),
          end: copyDate(this.innerModel.value[1] as Date),
        });
      } else if (this.formControl.value) {
        this.setInnerModel(this.formControl.value as RdrDateRange);
      }

      this.editable.saveEdit();
    }
  }

  onDatePicked(dates: (Date | undefined)[] | undefined): void {
    if (this.dp?.isOpen) {
      if (dates && isValidDate(dates[0] as Date) && isValidDate(dates[1] as Date)) {
        const start = DateTime.fromObject({
          year: dates[0]?.getFullYear(),
          month: (dates[0]?.getMonth() as number) + 1,
          day: dates[0]?.getDate(),
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });

        const end = DateTime.fromObject({
          year: dates[1]?.getFullYear(),
          month: (dates[1]?.getMonth() as number) + 1,
          day: dates[1]?.getDate(),
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        });

        this.formControl.setValue({ start: start.toISO(), end: end.toISO() });

        this.editable.saveEdit();
      }
    }
  }

  onEditableModeChange(mode: Mode) {
    this.isOpen = mode === 'edit';

    if (this.isOpen) {
      setTimeout(() => this.input?.nativeElement?.focus());
    }
  }

  onClear() {
    const defaultRange = this.props.isClearable
      ? undefined
      : { ...DEFAULT_PAGE_DATE_RANGE.dateRange };
    this.formControl.setValue(defaultRange);
    this.setInnerModel(defaultRange);
  }
}

const copyDate = (date: Date) => new Date(toISO8601(date));
